import { IAuthService, IUserInfo } from "@/services/auth/auth.types";
import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  BasicQueryStringUtils,
  DefaultCrypto,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  GRANT_TYPE_REFRESH_TOKEN,
  LocalStorageBackend,
  LocationLike,
  RedirectRequestHandler,
  StringMap,
  TokenRequest,
  TokenResponse,
} from "@openid/appauth";

const SSO_CLIENT_ID = process.env.VUE_APP_SSO_CLIENT_ID as string;
const SSO_AUTHORITY = process.env.VUE_APP_SSO_AUTHORITY as string;

class NoHashQueryStringUtils extends BasicQueryStringUtils {
  parse(input: LocationLike) {
    return super.parse(input, false);
  }
}

export class AuthService implements IAuthService {
  private readonly notifier: AuthorizationNotifier;
  private readonly authorizationHandler: RedirectRequestHandler;
  private readonly tokenHandler: BaseTokenRequestHandler;
  private configuration: AuthorizationServiceConfiguration | undefined;
  private code: string | undefined;
  private tokenResponse: TokenResponse | undefined;
  private tokenResponsePromise: Promise<TokenResponse> | undefined;

  constructor() {
    this.notifier = new AuthorizationNotifier();
    this.authorizationHandler = new RedirectRequestHandler(
      new LocalStorageBackend(),
      new NoHashQueryStringUtils(),
      window.location,
      new DefaultCrypto()
    );
    this.tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());

    const tokenResponseString = localStorage.getItem("token_response");
    this.tokenResponse = tokenResponseString
      ? new TokenResponse(JSON.parse(tokenResponseString))
      : undefined;

    this.notifier.setAuthorizationListener((request, response) => {
      if (response) {
        const extras: StringMap = {};
        if (request && request.internal) {
          extras.code_verifier = request.internal.code_verifier;
        }

        const tokenRequest = new TokenRequest({
          client_id: `${SSO_CLIENT_ID}`,
          redirect_uri: `${window.origin}/callback`,
          grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
          code: response.code,
          refresh_token: undefined,
          extras,
        });

        this.tokenResponsePromise = this.getConfiguration().then((configuration) => {
          return this.tokenHandler
            .performTokenRequest(configuration, tokenRequest)
            .then((r: TokenResponse) => {
              localStorage.setItem("token_response", JSON.stringify(r.toJson()));
              this.tokenResponse = r;
              return r;
            });
        });

        this.code = response.code;
      }
    });

    this.authorizationHandler.setAuthorizationNotifier(this.notifier);
  }

  private async getUserInfo(token: string): Promise<IUserInfo | null> {
    const configuration = await this.getConfiguration();
    const res = await fetch(configuration.userInfoEndpoint!, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    });

    const data = await res.json();

    return data ? (data as IUserInfo) : null;
  }

  public async startAuthorizationFlow(): Promise<void> {
    const request = new AuthorizationRequest({
      client_id: `${SSO_CLIENT_ID}`,
      redirect_uri: `${window.origin}/callback`,
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      scope: `openid ${SSO_CLIENT_ID}`,
    });

    const configuration = await this.getConfiguration();

    this.authorizationHandler.performAuthorizationRequest(configuration, request);
  }

  private isLoggedIn(): boolean {
    return !!this.tokenResponse && this.tokenResponse.isValid(0);
  }

  public async getUser(): Promise<IUserInfo | null> {
    return this.getUserInfo(this.tokenResponse?.accessToken || "");
  }

  public async getSignedIn(): Promise<boolean> {
    if (!this.isLoggedIn()) {
      await this.startAuthorizationFlow();
    } else {
      const configuration = await this.getConfiguration();
      await this.activateRefreshToken(configuration);
    }
    return this.isLoggedIn();
  }

  public async logout(): Promise<void> {
    localStorage.clear();
    window.location.href =
      (await this.getConfiguration()).endSessionEndpoint! +
      `?post_logout_redirect_uri=${window.origin}&id_token_hint=${this.tokenResponse?.idToken}`;
  }

  public async getIdToken(): Promise<string | undefined> {
    if (!this.isLoggedIn()) {
      await this.startAuthorizationFlow();
    }
    return this.tokenResponse?.idToken;
  }

  public async completeLogin(): Promise<void> {
    await this.authorizationHandler.completeAuthorizationRequestIfPossible();
    await this.tokenResponsePromise;
  }

  public getLogoutUri(): string | undefined {
    return this.configuration?.endSessionEndpoint;
  }

  private async getConfiguration(): Promise<AuthorizationServiceConfiguration> {
    if (this.configuration) {
      return Promise.resolve(this.configuration);
    }
    return AuthorizationServiceConfiguration.fetchFromIssuer(
      SSO_AUTHORITY,
      new FetchRequestor()
    ).then((response) => {
      this.configuration = response;
      return response;
    });
  }

  private async activateRefreshToken(
    configuration: AuthorizationServiceConfiguration
  ): Promise<void> {
    const expiredTime = this.tokenResponse?.expiresIn;
    const issuedAt = this.tokenResponse?.issuedAt;

    if (this.isLoggedIn() && expiredTime && issuedAt) {
      const dateIssuedAt = new Date(issuedAt * 1000);
      const dateExpiredAt = new Date(dateIssuedAt);
      const dateNow = new Date();
      dateExpiredAt.setSeconds(dateIssuedAt.getSeconds() + expiredTime);
      const expiredIn = (dateExpiredAt.getTime() - dateNow.getTime()) / 1000;

      const refreshTokenRequest = new TokenRequest({
        client_id: `${SSO_CLIENT_ID}`,
        redirect_uri: "",
        grant_type: GRANT_TYPE_REFRESH_TOKEN,
        code: undefined,
        refresh_token: this.tokenResponse?.refreshToken,
      });

      setTimeout(() => {
        this.tokenHandler
          .performTokenRequest(configuration, refreshTokenRequest)
          .then((r: TokenResponse) => {
            localStorage.setItem("token_response", JSON.stringify(r.toJson()));
            this.tokenResponse = r;
            this.activateRefreshToken(configuration);
          });
      }, (expiredIn - 60 || 0) * 1000);
    } else {
      await this.startAuthorizationFlow();
    }
  }
}
